import { ReactNode, useMemo, useRef } from "react";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  ListItemSecondaryAction,
  Paper,
  TextField,
} from "@material-ui/core";
import BackupIcon from "@material-ui/icons/Backup";
import DeleteIcon from "@material-ui/icons/Delete";
import FolderIcon from "@material-ui/icons/Folder";

import useAttachmentsPickerStyle from "./attachments-picker.style";

import { BucketAttachment } from "../../../graphql/models";
import { useUploadAttachment } from "../../../hooks";
import moveArrayItemAndReorder from "../../../utils/moveArrayItemAndReorder";

export interface AttachmentsPickerProps {
  title?: ReactNode;
  defaultValue?: BucketAttachment[];
  disabled?: boolean;
  size?: number;
  onUpdated: (attachments: BucketAttachment[]) => void;
}

export function AttachmentsPicker(props: AttachmentsPickerProps) {
  const { defaultValue, disabled, onUpdated, title } = props;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const maxLength = useMemo(() => props.size ?? Number.MAX_VALUE, []);
  const arraySize = useMemo(() => defaultValue?.length ?? 0, [defaultValue]);
  const classes = useAttachmentsPickerStyle();

  const triggerDeleteItem = (v: BucketAttachment) => {
    if (defaultValue) {
      const itemIndex = defaultValue?.findIndex((e) => e.url === v.url);
      const defaultValueCopy = [...(defaultValue ?? [])];
      defaultValueCopy.splice(itemIndex, 1);
      onUpdated([...defaultValueCopy]);
    }
  };

  const triggerNameChange = (newName: string, index: number) => {
    const attachmentsCopy = [...(defaultValue ?? [])];
    attachmentsCopy.splice(index, 1, {
      ...attachmentsCopy[index],
      name: newName,
    });

    onUpdated([...attachmentsCopy]);
  };

  const { uploadAttachment, computeAttachmentUrl } = useUploadAttachment(
    function onUploaded(attachment) {
      if (onUpdated) {
        const url = computeAttachmentUrl(attachment.id);
        onUpdated([
          ...(defaultValue ?? []),
          {
            url,
            name: url,
          },
        ]);
        // setNewAttachmentName("");
      }
    }
  );

  const uploadFileRef = useRef(null);

  const onFilePickerChange = ({
    target: {
      validity,
      files: [file],
    },
  }: any) => {
    if (validity.valid) {
      const { name } = file;
      file.filename = name;
      // setNewAttachmentName(name);
      uploadAttachment(file);
    }
  };

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === undefined) {
      return;
    }

    const { index: destinationIndex } = destination;
    const { index: sourceIndex } = source;

    const valueCopy = [...(defaultValue ?? [])];
    moveArrayItemAndReorder(valueCopy, sourceIndex, destinationIndex);

    onUpdated([...valueCopy]);
  };

  return (
    <Paper className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" className={classes.title}>
            {title ?? "Allegati"}
          </Typography>
          <List dense={false} style={{ width: "100%" }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId="attachments-reorderable"
                direction="vertical"
              >
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {defaultValue?.map((v, i) => (
                      <Draggable key={v.url} draggableId={v.url} index={i}>
                        {(provided) => (
                          <ListItem
                            style={{ width: "100%" }}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <ListItemAvatar>
                              <Avatar>
                                <IconButton onClick={() => window.open(v.url)}>
                                  <FolderIcon />
                                </IconButton>
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <TextField
                                  label="Nome del file"
                                  variant="outlined"
                                  type="text"
                                  style={{ width: "100%" }}
                                  defaultValue={v.name}
                                  onChange={(e) =>
                                    triggerNameChange(e.target.value, i)
                                  }
                                />
                              }
                            />
                            {provided.placeholder}
                            <ListItemSecondaryAction>
                              <IconButton
                                disabled={disabled}
                                onClick={() => triggerDeleteItem(v)}
                                edge="end"
                                aria-label="delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {!disabled && arraySize < maxLength && (
              <ListItem>
                <ListItemText primary={"Aggiungi un allegato"} />
                <ListItemSecondaryAction>
                  <input
                    hidden
                    required
                    disabled={disabled}
                    ref={uploadFileRef}
                    id="upload-file"
                    type="file"
                    onChange={onFilePickerChange}
                  />
                  <IconButton
                    disabled={disabled}
                    onClick={() =>
                      uploadFileRef.current &&
                      (uploadFileRef.current as any).click()
                    }
                    edge="end"
                    aria-label="upload"
                  >
                    <BackupIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    </Paper>
  );
}
